import { processRuntimeConfig } from '@helo/cms'
import {
  Assert,
  If,
  List,
  Record,
  Value,
  asyncFlow,
  flow,
  ifThen,
  pipe,
  removeEmptyKeys,
} from '@helo/utils'
import { isEqual } from 'lodash'

import { FilterCriteria } from '../../../services/queryService'
import { savedViewService } from '../../../services/savedViewService'
import { QueryConfig, WidgetConfig } from '../../../widgets/widget-types'
import { updateOrAppend } from '../../utils'
import { CmsSlice } from '../cms/cmsSlice'
import { PersistenceSlice } from '../persistence/persistenceSlice'
import { PersistenceSliceActions } from '../persistence/persistenceSlice.actions'
import { QuerySlice } from '../queryStore/querySlice'
import { findDataTypeConfig } from '../queryStore/querySlice.parts'
import { RouterSlice } from '../router/routerSlice'
import type { ViewSlice, ViewStore } from './viewSlice'
import {
  PartialViewState,
  PartialViewStateWithQuerySelector,
  ViewQueryState,
  ViewQueryUrlState,
  ViewState,
  ViewUrlState,
  WidgetState,
} from './viewSlice.types'

const removeEmptyProperties = <T extends Record<string, any>>(obj: T): T =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v !== undefined),
  ) as T

const pruneWidgetStateToCompare = (state?: Record<string, any>) => {
  if (!state) return
  return pipe(
    state,
    removeEmptyProperties,
    ifThen(Record.isEmptyRecord(), () => undefined),
  )
}

/**
 * Compares two widget state arrays to determine if they are equal.
 *
 * - If view has no widgets, no widget changes have been made
 * - Widgets in view but not in base = a change (new widget state)
 * - Widgets in both = compare their states via deep equality
 */
const areWidgetStatesEqual = (
  viewWidgetStates: WidgetState[],
  baseWidgetStates: WidgetState[],
): boolean => {
  if (viewWidgetStates.length === 0) return true

  return viewWidgetStates.every(widgetState => {
    const baseWidgetState = baseWidgetStates.find(
      base => base.widgetId === widgetState.widgetId,
    )
    if (!baseWidgetState) return false

    return isEqual(
      pruneWidgetStateToCompare(widgetState.state),
      pruneWidgetStateToCompare(baseWidgetState.state),
    )
  })
}

/**
 * Checks if a view query's variables represent changes from base.
 *
 * The view state stores DELTAS (changes from base), not complete state.
 * - Empty variables  `{}` means "no changes, use base values"
 * - Non-empty variables contain the actual changed values
 *
 * When variables are non-empty, we compare them against base using deep
 * equality to handle multi-sort and complex filter scenarios correctly.
 */
const hasQueryChanges = (
  viewQuery: ViewQueryState,
  baseQueryState: ViewQueryState | undefined,
): boolean => {
  const viewVars = viewQuery.variables ?? {}
  const viewKeys = Object.keys(viewVars)

  // Empty variables = no changes (delta is empty)
  if (viewKeys.length === 0) return false

  // If there's no base to compare against, any variables = changes
  if (!baseQueryState) return true

  // Non-empty variables exist - compare them against base
  // to determine if they actually differ
  const baseVars = baseQueryState.variables ?? {}

  // Check if any view variable differs from base
  return viewKeys.some(key => !isEqual(viewVars[key], baseVars[key]))
}

/**
 * Compares two query state arrays to determine if they are equal.
 *
 * Uses delta-based comparison: the view state stores only changes from base.
 * - If view has no queries, no query changes have been made
 * - Queries in view but not in base = a change (new query state)
 * - Queries in both = check if view's variables represent actual changes
 */
const areQueryStatesEqual = (
  viewQueryStates: ViewQueryState[],
  baseQueryStates: ViewQueryState[],
): boolean => {
  // If both have no queries, they're equal
  if (viewQueryStates.length === 0 && baseQueryStates.length === 0) {
    return true
  }

  // If counts differ, check if view has additional queries not in base
  if (viewQueryStates.length !== baseQueryStates.length) {
    // View has queries not in base = changes
    const hasNewQueries = viewQueryStates.some(
      viewQuery =>
        !baseQueryStates.some(base => base.queryId === viewQuery.queryId),
    )
    if (hasNewQueries) return false
  }

  // Check each view query for changes
  return viewQueryStates.every(viewQuery => {
    const baseQueryState = baseQueryStates.find(
      base => base.queryId === viewQuery.queryId,
    )

    // If no matching base query, it's a new query (change)
    if (!baseQueryState) return false

    // Check if this query has actual changes from base
    return !hasQueryChanges(viewQuery, baseQueryState)
  })
}

/**
 * Determines if the current view state matches the base state (no changes made).
 *
 * For the Save View button to be disabled by default on non-saved view table lists,
 * we need to correctly identify when no user changes have been made:
 * - Widgets in base but not in view = no change (view uses base/default values)
 * - Widgets in view but not in base = a change (new widget state)
 * - Widgets in both = compare their states
 * - Same logic applies to queries
 */
export const updateIsBaseState = () =>
  flow(flow.inputExtends<ViewSlice>(), <T extends ViewSlice>(store: T): T => {
    const baseWidgetStates = store.viewStore.baseViewState.widgetStates ?? []
    const viewWidgetStates = store.viewStore.viewState.widgetStates ?? []
    const viewQueryStates = store.viewStore.viewState.queryStates ?? []
    const baseQueryStates = store.viewStore.baseViewState.queryStates ?? []

    const areWidgetsEqual = areWidgetStatesEqual(
      viewWidgetStates,
      baseWidgetStates,
    )
    const areQueriesEqual = areQueryStatesEqual(
      viewQueryStates,
      baseQueryStates,
    )
    const isBaseState = areWidgetsEqual && areQueriesEqual

    return {
      ...store,
      viewStore: {
        ...store.viewStore,
        isBaseState,
      },
    }
  })

/**
 * TODO:
 * - pick widget states so base state can be updated too
 * - comment
 * - create effect-utils version of updateOrAppend, maybe upset or something.
 */
export const updateWidgetState =
  (widgetState: WidgetState) =>
  <T extends ViewState>(state: T): T => ({
    ...state,
    widgetStates: updateOrAppend(state.widgetStates, widgetState, 'widgetId'),
  })

/**
 * TODO:
 * - pick query states so base state can be updated too
 * - comment
 */
export const updateQueryState =
  (queryState: ViewQueryState) =>
  <T extends ViewState>(state: T): T => ({
    ...state,
    queryStates: updateOrAppend(state.queryStates, queryState, 'queryId'),
  })

/**
 * TODO:
 * - pick widgets states
 */
export const removeViewWidgetState =
  (widgetId: string) => (state: ViewStore) => {
    return {
      ...state,
      viewState: {
        ...state.viewState,
        widgetStates: state.viewState.widgetStates.filter(
          widgetState => widgetState.widgetId !== widgetId,
        ),
      },
    }
  }

/**
 * TODO:
 * - pick query states
 */
export const removeViewQueryState =
  (queryId: string) =>
  (state: ViewStore): ViewStore => ({
    ...state,
    viewState: {
      ...state.viewState,
      queryStates: state.viewState.queryStates.filter(
        queryState => queryState.queryId !== queryId,
      ),
    },
  })

export const persistView = () =>
  flow(
    flow.inputExtends<ViewSlice & PersistenceSlice>(),
    If.then(
      store => !!store.viewStore.savedView,
      store =>
        PersistenceSliceActions.persistItem({
          key: 'savedViewId',
          value: store.viewStore.savedView!.id,
          where: 'url',
          until: 'navigate',
        })(store),
    ),
    store =>
      PersistenceSliceActions.persistItem({
        key: 'viewState',
        value: pruneViewState(store.viewStore),
        where: 'url',
        until: 'navigate',
      })(store),
  )

export const getViewUrlState = () =>
  flow(
    flow.inputExtends<PersistenceSlice>(),
    PersistenceSliceActions.getPersistenceItemValue<ViewUrlState | undefined>(
      'viewState',
    ),
  )

const pruneQueryStates = (
  viewQueryStates: ViewQueryState[],
): ViewQueryState[] | undefined => {
  return pipe(
    viewQueryStates,
    List.filter(queryState => !Record.isEmptyRecord()(queryState.variables)),
    If.then(List.isEmpty(), () => undefined),
  )
}

const pruneWidgetStates = (
  widgetStates: WidgetState[],
  baseViewState: ViewState,
): WidgetState[] | undefined => {
  return pipe(
    widgetStates,
    widgetStates => {
      const finalResult = widgetStates
        .map(widgetStateItem => {
          const found = baseViewState.widgetStates.find(baseWidgetState => {
            return baseWidgetState.widgetId === widgetStateItem.widgetId
          })
          if (!found) {
            return widgetStateItem
          }
          if (isEqual(found, widgetStateItem)) {
            return undefined
          }
          return widgetStateItem
        })
        .filter(item => !!item)
      return finalResult
    },
    List.map(pruneWidgetState),
    List.filter(Value.isNotNullable),
    If.then(List.isEmpty(), () => undefined),
  )
}

/**
 * TODO: update prune to a ViewStoreToViewUrlState function.
 */
export const pruneViewState = (state: ViewStore): ViewUrlState | undefined => {
  return pipe(
    state,
    ({ viewState, baseViewState }): ViewUrlState => ({
      queryStates: pruneQueryStates(viewState.queryStates),
      widgetStates: pruneWidgetStates(viewState.widgetStates, baseViewState),
    }),
    Record.removeEmptyProperties(),
    If.then(Record.isEmptyRecord(), () => undefined),
  )
}

export const viewUrlStateToViewState = (urlState: ViewUrlState | undefined) => {
  return urlState ?? ({} as ViewUrlState)
}

const areFilterListsEqual = (
  listA: FilterCriteria[],
  listB: FilterCriteria[],
) => {
  return (
    List.every<FilterCriteria>(a => {
      const foundB = listB.find(b => b.columnId === a.columnId)

      return isEqual(a, foundB)
    })(listA) &&
    List.every<FilterCriteria>(b => {
      const foundA = listA.find(a => a.columnId === b.columnId)

      return isEqual(b, foundA)
    })(listB)
  )
}

/**
 * Reduces the size of the query state to limit the amount of data stored in the URL.
 *
 * if a base state exists, and the current state is equal, then the filter, sort, or params is removed.
 * if the base state does not exist, then the filter, sort, or params is removed if they contain no data.
 */
export const pruneQueryState =
  (state: ViewStore, isSavedView: boolean = false) =>
  (queryState: ViewQueryState): ViewQueryUrlState | undefined => {
    const baseQueryState = state.baseViewState.queryStates.find(
      baseQueryState => baseQueryState.queryId === queryState.queryId,
    ) ?? {
      queryId: queryState.queryId,
      variables: {},
    }

    const prunedVariables = Object.fromEntries(
      Object.entries(queryState.variables)
        .filter(([key, value]) => {
          return !isEqual(value, baseQueryState.variables[key])
        })
        .filter(([key, value]) => {
          const isObject =
            value && typeof value === 'object' && !Array.isArray(value)
          if (isObject) {
            return Object.keys(value).length > 0
          }
          return true
        })
        .filter(([key, value]) => value !== undefined),
    )

    if (Object.keys(prunedVariables).length === 0) {
      return undefined
    }

    return {
      ...baseQueryState,
      variables: prunedVariables,
    }
  }

/**
 * Reduces the size of the widget state before persistence to limit
 * the amount of data stored in the URL.
 *
 * Note: widgets and their state hook manage their own base value comparrison.
 */
export const pruneWidgetState = (
  widgetState: WidgetState,
): WidgetState | undefined =>
  pipe(
    widgetState,
    Assert.orReturn(Value.isNotNullable, () => undefined),
    widgetState => ({
      widgetId: widgetState.widgetId,
      state: pipe(
        widgetState.state,
        Assert.orReturn(Value.isNotNullable, () => undefined),
        Record.removeEmptyProperties(),
        If.then(Record.isEmptyRecord(), () => undefined),
      ),
    }),
    If.then(
      widgetState => !Value.isNotNullable(widgetState.state),
      () => undefined,
    ),
  )

/**
 * TODO: Make suer we're using this whereever we're injesting query state into the app.
 */
// export const createQueryState = (
//   queryState: Partial<ViewQueryState>,
// ): ViewQueryState => {
//   return {
//     ...DEFAULT_QUERY_STATE,
//     ...queryState,
//     ...(queryState?.filterCriteria?.length && {
//       filterCriteria: queryState.filterCriteria.map(createFilter),
//     }),
//   }
// }

/**
 * TODO: make sure this is used whereever we're injesting filter information. content, urls.
 */
// export const createFilter = (
//   filter: Partial<FilterCriteria>,
// ): FilterCriteria => ({
//   columnId: '',
//   operator: FilterOperator.AND,
//   condition: FilterCondition.Contains,
//   filterValue: '',
//   filterValueType: FilterValueType.Text,
//   ...filter,
// })

// export const DEFAULT_QUERY_STATE: ViewQueryState = {
//   sortCriteria: [],
//   filterCriteria: [],
//   queryId: '',
//   querySelector: [],
// }

type NonFunctionPropertyNames<T> = {
  [K in keyof T]: T[K] extends Function ? never : K
}[keyof T]

type T = NonFunctionPropertyNames<ViewStore>

type ExcludeFunctionProperties<T> = Pick<T, NonFunctionPropertyNames<T>>

export const DEFAULT_VIEW_STORE_STATE: ExcludeFunctionProperties<ViewStore> = {
  baseViewState: {
    queryStates: [],
    widgetStates: [],
  },
  contentViewState: {
    queryStates: [],
    widgetStates: [],
  },
  viewUrlState: {},
  viewState: {
    queryStates: [],
    widgetStates: [],
  },
  savedView: null,
  savedViewId: undefined,
  viewId: null,
  isBaseState: true,
}

/** Will use the new query state filter sort or params only if they exist. */
const mergeQueryState =
  (newQueryState: Partial<ViewQueryState>) =>
  (originalQueryState: Partial<ViewQueryState>): Partial<ViewQueryState> => {
    return {
      ...originalQueryState,
      ...newQueryState,
      variables: {
        ...originalQueryState.variables,
        ...newQueryState.variables,
      },
    }
  }

const mergeQueryStates =
  (newViewState: Partial<ViewState>) =>
  (originalViewState: Partial<ViewState>): Partial<ViewState> => {
    return {
      ...originalViewState,
      ...(newViewState.queryStates && {
        queryStates: (newViewState.queryStates ?? []).reduce(
          (prevQueryStates, newQueryState): ViewQueryState[] => {
            // find in original list
            const foundQueryState = prevQueryStates.find(
              queryState => queryState.queryId === newQueryState.queryId,
            )
            if (!foundQueryState) {
              return [...prevQueryStates, newQueryState]
            }

            const updatedQueryState = mergeQueryState(newQueryState)(
              foundQueryState,
            ) as ViewQueryState

            // replace the original and return
            return prevQueryStates.map(queryState =>
              queryState.queryId === newQueryState.queryId
                ? updatedQueryState
                : queryState,
            )
          },
          originalViewState.queryStates ?? [],
        ),
      }),
    }
  }

const mergeWidgetState =
  (newWidgetState: WidgetState) => (originalWidgetState: WidgetState) => {
    return {
      ...originalWidgetState,
      ...newWidgetState,
      state: {
        ...originalWidgetState.state,
        ...newWidgetState.state,
      },
    }
  }

const mergeWidgetStates =
  (newViewState: Partial<ViewState>) =>
  (originalViewState: Partial<ViewState>) => {
    return {
      ...originalViewState,
      ...(newViewState.widgetStates && {
        widgetStates: newViewState.widgetStates.reduce(
          (prevWidgetStates, newWidgetState) => {
            // find in original list
            const foundWidgetState = prevWidgetStates.find(
              widgetState => widgetState.widgetId === newWidgetState.widgetId,
            )

            if (!foundWidgetState) {
              return [...prevWidgetStates, newWidgetState]
            }

            const updatedWidgetState = mergeWidgetState(newWidgetState)(
              foundWidgetState,
            ) as WidgetState

            return prevWidgetStates.map(widgetState =>
              widgetState.widgetId === newWidgetState.widgetId
                ? updatedWidgetState
                : widgetState,
            )
          },
          originalViewState.widgetStates ?? [],
        ),
      }),
    }
  }

/**
 * Merges view states, and also does a deep merge on query and widget states
 *
 * Will add or replace query or widget states to the existing list.
 * 
 * A deep merge on a query state will combine sorts and filters if
 * they exist. This allows sorts and filters to be added independently
 * of eachother

 */
export const deepMergeViewState =
  (newViewState: Partial<ViewState>) =>
  (originalViewState: Partial<ViewState>) =>
    pipe(
      originalViewState,
      mergeQueryStates(newViewState),
      mergeWidgetStates(newViewState),
    )

/**
 * A simple overwrite merge for the base props.
 *
 * Will add or replace query or widget states.
 */
export const mergeViewState =
  (newViewState: Partial<ViewState>) => (originalViewState: ViewState) =>
    pipe(
      originalViewState,
      viewState =>
        (newViewState.queryStates ?? []).reduce((prevViewState, queryState) => {
          return updateQueryState(queryState)(prevViewState)
        }, viewState),
      viewState =>
        (newViewState.widgetStates ?? []).reduce(
          (prevViewState, widgetState) => {
            return updateWidgetState(widgetState)(prevViewState)
          },
          viewState,
        ),
    )

export const recursivelyExtractQueries = (
  widgetConfig: WidgetConfig,
): QueryConfig[] => {
  const queries = widgetConfig.queries ?? []
  const childrenQueries = (widgetConfig.children ?? []).flatMap(
    recursivelyExtractQueries,
  )

  return [...queries, ...childrenQueries]
}

/**
 * Remove any query states that are not included in the content registration.
 *
 * This avoids saved views or URLs from adding queries that should not run or
 * that were deprecated.
 */
export const filterQueryStatesByContentQueries =
  (contentViewState: ViewState) =>
  (viewState: ViewState): ViewState => {
    return {
      ...viewState,
      queryStates: viewState.queryStates.filter(queryState =>
        contentViewState.queryStates.some(
          contentQueryState => contentQueryState.queryId === queryState.queryId,
        ),
      ),
    }
  }

export const loadContentViewState = () =>
  flow(
    flow.inputExtends<ViewSlice & RouterSlice & QuerySlice>(),
    <T extends ViewSlice & RouterSlice & QuerySlice>(store: T): T => {
      const viewConfigState: Partial<ViewState> = {
        queryStates: [
          ...(store.router.routeConfig?.viewConfig?.queries ?? []).map(
            query => ({
              queryId: query.id,
              variables: processRuntimeConfig(query.variables ?? {}) as any,
            }),
          ),
        ],
        widgetStates: [],
      }

      return {
        ...store,
        viewStore: {
          ...store.viewStore,
          contentViewState: viewConfigState,
        },
      }
    },
  )

export const pullViewUrlState = () =>
  flow(
    flow.inputExtends<ViewSlice & PersistenceSlice>(),
    <T extends ViewSlice & PersistenceSlice>(store: T): T => {
      const urlState =
        PersistenceSliceActions.getPersistenceItemValue<ViewUrlState>(
          'viewState',
        )(store)

      const savedViewId =
        PersistenceSliceActions.getPersistenceItemValue<string>('savedViewId')(
          store,
        )

      const viewUrlState = viewUrlStateToViewState(urlState)

      return {
        ...store,
        viewStore: {
          ...store.viewStore,
          savedViewId,
          viewUrlState,
        },
      }
    },
  )

export const getSavedView = () =>
  asyncFlow(
    asyncFlow.inputExtends<ViewSlice>(),
    async <T extends ViewSlice>(store: T): Promise<T> => {
      if (!store.viewStore.savedViewId) return store

      const savedView = await savedViewService.getSavedView(
        store.viewStore.savedViewId,
      )

      return {
        ...store,
        viewStore: {
          ...store.viewStore,
          savedView,
        },
      }
    },
  )

export const setupBaseViewState = () =>
  flow(
    flow.inputExtends<ViewSlice & QuerySlice>(),
    <T extends ViewSlice>(store: T): T => ({
      ...store,
      viewStore: {
        ...store.viewStore,
        baseViewState: pipe(
          store.viewStore.contentViewState,
          If.then(
            () => !Value.isNullable(store.viewStore.savedView?.viewState),
            deepMergeViewState(store.viewStore.savedView?.viewState ?? {}),
          ),
        ),
      },
    }),
  )

export const setupRuntimeViewState = () =>
  flow(
    flow.inputExtends<ViewSlice & QuerySlice>(),
    <T extends ViewSlice>(store: T): T => {
      const runtimeState = pipe(
        store.viewStore.baseViewState,
        deepMergeViewState(store.viewStore.viewUrlState),
      )

      return {
        ...store,
        viewStore: {
          ...store.viewStore,
          viewState: runtimeState,
        },
      }
    },
  )
